import { ViewerConfigDto } from "../dto/viewer-config.dto";
import { WidgetConfigDto } from "../dto/widget-config.dto";
import { CreateTrillionUserDataDto } from "../dto/create-trillion-user-data.dto";
import { NoBillingPackageError } from "./errors/NoBillingPackageError";
import { FetchingConfigError } from "./errors/FetchingConfigError";
import {IncorrectActivationKeyError} from './errors/IncorrectActivationKeyError';
import {ReflectionMapDto} from "../dto/reflection-map.dto";
import {FetchingReflectionMapsError} from "./errors/FetchingReflectionMapsError";
import {ProductNotExistOrNotPublishedError} from "./errors/NoProductExistOrNotPublished";

export class TrillionBackendApi {
  private _baseUrl: string;
  private _staticConfigsUrl: string | undefined = undefined;
  private _serviceActivationKey: string | null = null;

  static prodUrl = "https://dashboard.trillion.jewelry/api/"
  static devUrl = "https://dev.dashboard.trillion.jewelry/api/"

  constructor() {
    this._baseUrl = this.getDefaultBaseUrl();
  }

  getDefaultBaseUrl(): string {
    if (import.meta.env.VITE_TRILLION_API_BASE_URL) {
      return import.meta.env.VITE_TRILLION_API_BASE_URL as string;
    } else {
      return import.meta.env.VITE_IS_LIBRARY_MODE
        ? TrillionBackendApi.prodUrl
        : TrillionBackendApi.devUrl;
    }
  }

  changeBaseURL(url: string | null) {
    this._baseUrl = url ?? this.getDefaultBaseUrl();
  }

  setServiceActivationKey(key: string | null) {
    this._serviceActivationKey = key
  }

  async get<TResponse>(path: string, params?: { [key: string]: string }, signal?: AbortSignal) {
    let json;
    let response;
    let searchParams = params ? `?${new URLSearchParams(params)}` : "";
    try {
      response = await fetch(`${this._baseUrl}/${path}${searchParams}`, {
        headers: {
          "Content-Type": "application/json;charset=utf-8",
        },
        signal,
      });
      json = await response.json();
    } catch (error) {
      if (error instanceof SyntaxError) {
        // Unexpected token < in JSON
        console.log("There was a SyntaxError", error);
      } else if ((error as Error).name === 'AbortError') {
      } else {
        console.log("There was an error", error);
      }
      throw new Error("Fetch error", { cause: error });
    }

    return { response, json: json as TResponse };
  }

  async post<TResponse, TBody>(path: string, body: TBody) {
    let json;
    let response;
    try {
      response = await fetch(`${this._baseUrl}/${path}`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json;charset=utf-8",
        },
        body: JSON.stringify(body),
      });
      json = await response.json();
    } catch (error) {
      if (error instanceof SyntaxError) {
        // Unexpected token < in JSON
        console.log("There was a SyntaxError", error);
      } else {
        console.log("There was an error", error);
      }
      throw new Error("Fetch error", { cause: error });
    }

    return { response, json: json as TResponse };
  }

  async getAllReflectionMaps(): Promise<ReflectionMapDto> {
    const { response, json } = await this.get<ReflectionMapDto>(
      `trillionwebapp/reflection-maps/all`,
    );
    if (response?.ok) {
      return json;
    } else {
      throw new FetchingReflectionMapsError();
    }
  }

  async getProductConfigForViewer(sku: string, signal?: AbortSignal): Promise<ViewerConfigDto> {
    let params = undefined;

    if (this._serviceActivationKey) {
      params = {
        key: this._serviceActivationKey,
      };
    }

    const { response, json } = await this.get<ViewerConfigDto>(
      `trillionwebapp/config/viewer/${sku}`,
      params,
      signal
    );
    if (response?.ok) {
      return json;
    } else {

      if (response.status === 403) {
        throw new NoBillingPackageError("viewer");
      } else if (response.status === 401) {
        throw new IncorrectActivationKeyError()
      } else if (response.status === 406) {
        throw new ProductNotExistOrNotPublishedError()
      }

      throw new FetchingConfigError("viewer");
    }
  }

  async getProductConfigsForViewer(SKUs: string[]): Promise<ViewerConfigDto[]> {
    const params: { [key: string]: string } = {
      ids: SKUs.join(","),
    };

    if (this._serviceActivationKey) {
      params.key = this._serviceActivationKey;
    }

    const { response, json } = await this.get<ViewerConfigDto[]>(
      `trillionwebapp/config/viewer`,
      params
    );
    if (response?.ok) {
      return json;
    } else {
      if (response.status === 403) {
        throw new NoBillingPackageError("viewer");
      } else if (response.status === 401) {
        throw new IncorrectActivationKeyError()
      } else if (response.status === 406) {
        throw new ProductNotExistOrNotPublishedError()
      }

      throw new FetchingConfigError("viewer");
    }
  }

  async getProductConfigForWidget(sku: string): Promise<WidgetConfigDto> {
    let params = undefined;

    if (this._serviceActivationKey) {
      params = {
        key: this._serviceActivationKey,
      };
    }

    const { response, json } = await this.get<WidgetConfigDto>(
      `trillionwebapp/config/widget/${sku}`,
      params
    );
    if (response?.ok) {
      return json;
    } else {
      if (response.status === 403) {
        throw new NoBillingPackageError("widget");
      } else if (response.status === 401) {
        throw new IncorrectActivationKeyError()
      } else if (response.status === 406) {
        throw new ProductNotExistOrNotPublishedError()
      }

      throw new FetchingConfigError("widget");
    }
  }

  async getProductConfigsForWidget(SKUs: string[]): Promise<WidgetConfigDto[]> {
    if (this._staticConfigsUrl) {
      return this.getProductConfigsFromStaticConfig(SKUs);
    }

    const params: { [key: string]: string } = {
      ids: SKUs.join(","),
    };

    if (this._serviceActivationKey) {
      params.key = this._serviceActivationKey;
    }

    const { response, json } = await this.get<WidgetConfigDto[]>(
      `trillionwebapp/config/widget`,
      params
    );
    if (response?.ok) {
      return json;
    } else {
      if (response.status === 403) {
        throw new NoBillingPackageError("widget");
      } else if (response.status === 401) {
        throw new IncorrectActivationKeyError()
      } else if (response.status === 406) {
        throw new ProductNotExistOrNotPublishedError()
      }

      throw new FetchingConfigError("widget");
    }
  }

  async saveUserData(email: string, url: string) {
    const data = new CreateTrillionUserDataDto(email, url);
    const { response } = await this.post("/trillionwebapp/userData", data);

    if (!response?.ok) {
      console.error("Error while saving user data", response.statusText);
    }
  }

  setStaticConfigsUrl(url: string) {
    this._staticConfigsUrl = url;
    this._baseUrl = "";
  }

  async getProductConfigsFromStaticConfig(
    SKU: string[]
  ): Promise<WidgetConfigDto[]> {
    if (!this._staticConfigsUrl)
      throw new Error("Static configs url is not set");
    const { response, json } = await this.get<WidgetConfigDto[]>(
      this._staticConfigsUrl
    );
    if (response?.ok) {
      return json.filter((config) => SKU.includes(config.sku));
    } else {
      throw new FetchingConfigError("widget");
    }
  }
}

export const TrillionBackend = new TrillionBackendApi();
